import { Text, Textarea } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { type TransformedValues, useForm } from '@mantine/form'
import { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { SelectCreatable } from '#src/components/input/SelectCreatable.tsx'
import { SDRIDTPractitionerSelect } from '#src/components/sdr/display/SDRIDTPractitionerSelect.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

type Props = {
  sdr: SDR
}

type SDRIDTReviewData = {
  // TODO: better handling of date types
  reviewDate: Date | undefined
  informedMethod: SDR['reviewInformedMethod']
  involvedIDTMember: SDR['reviewInvolvedIDTMember']
  notes: SDR['reviewNotes']
}

export function SDRIDTReview({ sdr }: Props): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const form = useForm<SDRIDTReviewData>({
    mode: 'uncontrolled',
    initialValues: {
      reviewDate: sdr.reviewDate ? new Date(sdr.reviewDate) : new Date(),
      informedMethod: sdr.reviewInformedMethod,
      involvedIDTMember: sdr.reviewInvolvedIDTMember,
      notes: sdr.reviewNotes,
    },

    // functions will be used to validate values at corresponding key
    validate: {
      informedMethod: (value = '') =>
        value.length > 0 ? null : 'Informed method is required',
      involvedIDTMember: (value) =>
        value !== undefined ? null : 'At least one IDT member must be selected',
    },
  })

  const patchMutation = usePatchMutation<SDR>()

  const submitSDRIDTReview = form.onSubmit(
    (newSDR: TransformedValues<typeof form>) => {
      patchMutation.mutate({
        instance: sdr,
        mutateFn: (sdr) => {
          sdr.reviewDate = newSDR.reviewDate?.toISOString()
          sdr.reviewInformedMethod = newSDR.informedMethod
          sdr.reviewInvolvedIDTMember = newSDR.involvedIDTMember
          sdr.reviewNotes = newSDR.notes
        },
      })
    },
    (err) => {
      console.error('Error:', err)
    }
  )

  const formErrors = Object.values(form.errors).filter(Boolean)

  return (
    <form onSubmit={submitSDRIDTReview}>
      <div className="flex flex-col gap-4">
        <div className="text-lg font-semibold">Review of SDR with IDT</div>
        <SDRIDTPractitionerSelect
          label="Reviewer"
          key={form.key('involvedIDTMember')}
          {...form.getInputProps('involvedIDTMember')}
        />

        <div className="flex flex-row gap-4">
          <DateTimePicker
            label="Review Date"
            className="grow"
            valueFormat="DD MMM YYYY hh:mm A"
            key={form.key('reviewDate')}
            {...form.getInputProps('reviewDate')}
          />
          {/* TODO: Hide if reviewer isn't IDT after SRR */}
          <SelectCreatable
            initialOptions={[SDR.DEFAULT_INFORMED_METHOD]}
            className="grow"
            label="How was IDT informed of the SDR?"
            key={form.key('informedMethod')}
            {...form.getInputProps('informedMethod')}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Textarea
            label="Notes"
            placeholder="Recommended meeting notes"
            key={form.key('notes')}
            {...form.getInputProps('notes')}
          />

          {formErrors.length > 0 && (
            <ul className="text-sm text-red-500">
              {formErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          )}

          <Button
            type="submit"
            loading={patchMutation.isPending}
            disabled={isReadOnly}
            className="ml-auto"
          >
            Submit
          </Button>
        </div>

        {patchMutation.isError && (
          <Text c="red" className="text-sm">
            Failed to set the IDT review data:{' '}
            {patchMutation.error instanceof Error
              ? patchMutation.error.message
              : 'An error occurred'}
          </Text>
        )}
      </div>
    </form>
  )
}
